import qs from "query-string";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  ensureTrailingSlash,
  generateSpecificSearchPattern,
  getBaseUrl,
  RideProductionRoutes,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  tableSearchAF,
  usePrefixedRouting,
} from "@ryde/shared";

import { useDynamicFormsFetch } from "../use-dynamic-forms-fetch";

const FETCH_RIDE_PRODUCTIONS_DELAY_MS = 100;

type TRideProductionsByIdsNavigation = {
  navigateToRideProductionsByIds: (productionIds: number[]) => void;
  openRideProductionsByIdsInNewTab: (productionIds: number[]) => void;
};

export const useRideProductionsByIdsNavigation =
  (): TRideProductionsByIdsNavigation => {
    const { navigatePrefixed, addPrefix } = usePrefixedRouting();
    const setTableSearch = useSetRecoilState(tableSearchAF("rideProduction"));
    const setStarredDisplayMode = useSetRecoilState(
      starredDisplayModeAtomFamily("rideProduction"),
    );
    const { fetchTableRowsServerFiltered: fetchRideProductions } =
      useDynamicFormsFetch("rideProduction");

    const navigateToRideProductionsByIds = useEvent(
      (productionIds: number[]): void => {
        if (!productionIds?.length) {
          return;
        }
        setStarredDisplayMode(StarredDisplayModeEnum.ShowAllIncludeStarred);
        setTableSearch(generateSpecificSearchPattern(productionIds));
        navigatePrefixed(RideProductionRoutes.SEARCH_GRID);
        setTimeout(
          () => fetchRideProductions(),
          FETCH_RIDE_PRODUCTIONS_DELAY_MS,
        );
      },
    );

    /**
     * Opens ride-productions grid with `id:` search in a new browser tab.
     * Hash URL + `search` QS is hydrated by the grid on load (same idea as rides/courses).
     */
    const openRideProductionsByIdsInNewTab = useEvent(
      (productionIds: number[]): void => {
        if (!productionIds?.length) {
          return;
        }
        const searchPattern = generateSpecificSearchPattern(productionIds);
        const relativePath = addPrefix(
          `${RideProductionRoutes.SEARCH_GRID}?${qs.stringify({ search: searchPattern })}`,
        );
        const rideProductionsUrl = `${ensureTrailingSlash(getBaseUrl())}#${relativePath}`;
        window.open(rideProductionsUrl, "_blank");
      },
    );

    const navigation: TRideProductionsByIdsNavigation = {
      navigateToRideProductionsByIds,
      openRideProductionsByIdsInNewTab,
    };
    return navigation;
  };
